import m from "mithril";
import { getUserData, postMithril, getMithril } from "../utils.js";
import toast from "../js/toaster.js";

var CoordinatorFundManagement = {
    coordinators: [],
    selectedCoordinator: null,
    formData: {
        amount: '',
        amountDisplay: '',
        description: '',
        transaction_type: ''
    },
    loading: false,
    submitting: false,

    oninit: async function () {
        const userData = await getUserData();
        if (!userData || (userData.role !== 'manajer' && userData.role !== 'superuser' && userData.role !== 'kasir')) {
            m.route.set('/unauthorized');
            return;
        }
        this.loadCoordinators();
    },

    loadCoordinators: function () {
        this.loading = true;
        getMithril('/api/coordinator-fund-management', { component: this })
            .then(response => {
                if (response.success) {
                    this.coordinators = response.data.map(coord => ({
                        ...coord,
                        balance: parseFloat(coord.balance || 0)
                    }));
                } else {
                    throw new Error(response.error || 'Gagal memuat koordinator');
                }
            })
            .catch(error => {
                console.error('Error loading officers:', error);
                toast.error('Gagal memuat data petugas: ' + error.message);
            })
            .finally(() => {
                this.loading = false;
                m.redraw();
            });
    },

    selectCoordinator: function (coordinator) {
        this.selectedCoordinator = coordinator;
        this.formData = {
            amount: '',
            amountDisplay: '',
            description: '',
            transaction_type: ''
        };
        m.redraw();
    },

    submitTransaction: function () {
        if (!this.selectedCoordinator) {
            toast.warning('Pilih petugas terlebih dahulu');
            return;
        }

        if (!this.formData.transaction_type) {
            toast.warning('Pilih jenis transaksi terlebih dahulu');
            return;
        }

        if (!this.formData.amount || parseFloat(this.formData.amount) <= 0) {
            toast.warning('Masukkan jumlah yang valid');
            return;
        }

        if (!this.formData.description.trim()) {
            toast.warning('Masukkan deskripsi transaksi');
            return;
        }

        this.submitting = true;
        postMithril('/api/coordinator-fund-management', {
            officer_id: this.selectedCoordinator.officer_id,
            transaction_type: this.formData.transaction_type,
            amount: parseFloat(this.formData.amount),
            description: this.formData.description.trim()
        }, { component: this })
            .then(response => {
                if (response.success) {
                    toast.success(response.message);
                    this.selectedCoordinator = null;
                    this.formData = {
                        amount: '',
                        amountDisplay: '',
                        description: '',
                        transaction_type: ''
                    };
                    // Reload coordinators to get updated balances
                    this.loadCoordinators();
                } else {
                    throw new Error(response.error || 'Transaksi gagal');
                }
            })
            .catch(error => {
                console.error('Error submitting transaction:', error);
                const translatedMessage = error.message === 'Reimbursement amount cannot exceed the deficit'
                    ? 'Jumlah penggantian tidak boleh melebihi defisit'
                    : error.message;
                toast.error('Error: ' + translatedMessage);
            })
            .finally(() => {
                this.submitting = false;
                m.redraw();
            });
    },

    formatCurrency: function (amount) {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR'
        }).format(amount);
    },

    formatNumberInput: function (value) {
        const num = parseFloat(value.replace(/[^\d.,]/g, '').replace(',', '.'));
        if (isNaN(num)) return '';
        return num.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },

    unformatNumberInput: function (value) {
        return value.replace(/\./g, '');
    },

    exportToExcel: function () {
        if (!this.coordinators || this.coordinators.length === 0) {
            toast.error("Tidak ada data untuk diekspor");
            return;
        }

        const headers = ["Nama Petugas", "Kode", "Subunit", "Saldo", "Status"];
        // Numbers must be unquoted in CSV so Excel parses them as numbers (not text).
        const csvText = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const csvNum = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : '0';
        };
        const rows = this.coordinators.map(c => {
            const balance = parseFloat(c.balance || 0);
            const status = balance > 0 ? "Menyimpan Dana" : balance < 0 ? "Perlu Penggantian" : "Nihil";
            return [csvText(c.officer_name), csvText(c.officer_code), csvText(c.subunit_name || '-'), csvNum(balance), csvText(status)].join(',');
        });

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", `Dana_Petugas_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },

    view: function () {
        const totalNet = this.coordinators.reduce((sum, c) => sum + (parseFloat(c.balance) || 0), 0);
        const totalSurplus = this.coordinators.filter(c => c.balance > 0).reduce((sum, c) => sum + parseFloat(c.balance), 0);
        const totalDeficit = this.coordinators.filter(c => c.balance < 0).reduce((sum, c) => sum + Math.abs(parseFloat(c.balance)), 0);

        return m("div", { class: "max-w-7xl mx-auto p-6" }, [
            m("div", { class: "mb-8" }, [
                m("div", { class: "flex items-center justify-between mb-2" }, [
                    m("div", { class: "flex items-center space-x-4" }, [
                        m("div", { class: "w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg" }, [
                            m("i", { class: "ri-exchange-funds-line text-white text-xl" })
                        ]),
                        m("div", [
                            m("h1", { class: "text-3xl font-bold text-gray-900 dark:text-white" }, "Penggantian/Penitipan Dana"),
                            m("p", { class: "text-lg text-gray-600 dark:text-gray-400" }, "Kelola dana petugas melalui penitipan atau penggantian")
                        ])
                    ]),
                    m("button", {
                        onclick: () => this.exportToExcel(),
                        class: "inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg shadow-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    }, [
                        m("i", { class: "ri-file-excel-2-line mr-2" }),
                        "Export Data"
                    ])
                ]),
                m("div", { class: "h-1 w-20 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full" })
            ]),

            // Stats Headers
            m("div", { class: "grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" }, [
                m("div", { class: "bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700" }, [
                    m("p", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Dana di Petugas"),
                    m("p", { class: "text-2xl font-bold text-green-600" }, this.formatCurrency(totalSurplus))
                ]),
                m("div", { class: "bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700" }, [
                    m("p", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Defisit (Perlu Penggantian)"),
                    m("p", { class: "text-2xl font-bold text-red-600" }, this.formatCurrency(totalDeficit))
                ]),
                m("div", { class: "bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700" }, [
                    m("p", { class: "text-sm font-medium text-gray-500 dark:text-gray-400" }, "Posisi Netto"),
                    m("p", { class: `text-2xl font-bold ${totalNet >= 0 ? 'text-blue-600' : 'text-orange-600'}` }, this.formatCurrency(totalNet))
                ]),
            ]),

            // Coordinators Table with Accordion
            m("div", { class: "bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700" }, [
                m("div", { class: "overflow-x-auto" }, [
                    m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                        m("thead", { class: "bg-gradient-to-r from-orange-50 to-orange-100 dark:from-gray-700 dark:to-gray-600" }, [
                            m("tr", [
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider" }, "Nama Petugas"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider" }, "Unit/Kode"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider" }, "Saldo"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider" }, "Aksi")
                            ])
                        ]),
                        m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" }, [
                            this.loading && !this.coordinators.length && m("tr", [
                                m("td", { class: "px-6 py-8 whitespace-nowrap text-center text-sm text-gray-500", colspan: "4" }, [
                                    m("i", { class: "ri-loader-4-line animate-spin text-3xl text-orange-600 mb-2" }),
                                    m("p", "Memuat data...")
                                ])
                            ]),
                            !this.loading && this.coordinators.length === 0 && m("tr", [
                                m("td", { class: "px-6 py-8 whitespace-nowrap text-center text-sm text-gray-500", colspan: "4" }, "Tidak ada petugas")
                            ]),
                            this.coordinators.reduce((acc, coordinator, index) => {
                                const balance = parseFloat(coordinator.balance || 0);
                                const isExpanded = this.selectedCoordinator && this.selectedCoordinator.officer_id === coordinator.officer_id;

                                acc.push(m("tr", {
                                    key: coordinator.officer_id,
                                    class: index % 2 === 0 ? "bg-white dark:bg-gray-800" : "bg-gray-50 dark:bg-gray-750"
                                }, [
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white" }, coordinator.officer_name),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" }, [
                                        m("div", coordinator.subunit_name || coordinator.subunit_id),
                                        m("div", { class: "text-xs text-gray-400" }, coordinator.officer_code)
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-bold" }, [
                                        m("span", { class: balance >= 0 ? 'text-green-600' : 'text-red-600' }, this.formatCurrency(balance))
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium" }, [
                                        m("button", {
                                            onclick: () => this.selectCoordinator(isExpanded ? null : coordinator),
                                            class: `inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isExpanded
                                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                                                : 'bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-300'
                                                }`
                                        }, [
                                            m("i", { class: `ri-${isExpanded ? 'arrow-up-s' : 'edit'}-line mr-1` }),
                                            isExpanded ? "Tutup" : "Kelola"
                                        ])
                                    ])
                                ]));

                                if (isExpanded) {
                                    acc.push(m("tr", { key: `expanded-${coordinator.officer_id}` }, [
                                        m("td", { colspan: "4", class: "px-0 py-0" }, [
                                            m("div", { class: "bg-gray-50 dark:bg-gray-900 p-6 border-y border-gray-200 dark:border-gray-700 shadow-inner" }, [
                                                m("div", { class: "max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700" }, [
                                                    m("h3", { class: "text-lg font-bold text-gray-900 dark:text-white mb-4" }, "Form Transaksi"),

                                                    // Transaction Type Selection
                                                    m("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" }, [
                                                        // Entrusted Funds Option
                                                        balance >= 0 ? m("button", {
                                                            class: `p-4 border-2 rounded-lg text-left transition-all ${this.formData.transaction_type === 'entrusted_funds'
                                                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                                                                : 'border-gray-200 hover:border-orange-300'
                                                                }`,
                                                            onclick: () => {
                                                                this.formData.transaction_type = 'entrusted_funds';
                                                                this.formData.amount = '';
                                                                this.formData.amountDisplay = '';
                                                            }
                                                        }, [
                                                            m("div", { class: "flex items-center mb-2" }, [
                                                                m("div", { class: "p-2 bg-green-100 rounded-full mr-3" }, [
                                                                    m("i", { class: "ri-add-circle-line text-green-600 text-xl" })
                                                                ]),
                                                                m("span", { class: "font-semibold text-gray-900 dark:text-white" }, "Penitipan Dana")
                                                            ]),
                                                            m("p", { class: "text-sm text-gray-500 ml-12" }, "Menyerahkan dana operasional ke petugas")
                                                        ]) : m("div", { class: "p-4 border-2 border-gray-100 rounded-lg opacity-50 bg-gray-50 cursor-not-allowed" }, [
                                                            m("div", { class: "font-medium text-gray-400" }, "Penitipan Dana"),
                                                            m("div", { class: "text-xs" }, "Tidak tersedia (Saldo Negatif)")
                                                        ]),

                                                        // Reimbursement Option
                                                        balance < 0 ? m("button", {
                                                            class: `p-4 border-2 rounded-lg text-left transition-all ${this.formData.transaction_type === 'reimbursement'
                                                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                                                                : 'border-gray-200 hover:border-orange-300'
                                                                }`,
                                                            onclick: () => {
                                                                this.formData.transaction_type = 'reimbursement';
                                                                this.formData.amount = '';
                                                                this.formData.amountDisplay = '';
                                                            }
                                                        }, [
                                                            m("div", { class: "flex items-center mb-2" }, [
                                                                m("div", { class: "p-2 bg-blue-100 rounded-full mr-3" }, [
                                                                    m("i", { class: "ri-refund-2-line text-blue-600 text-xl" })
                                                                ]),
                                                                m("span", { class: "font-semibold text-gray-900 dark:text-white" }, "Penggantian (Reimburse)")
                                                            ]),
                                                            m("p", { class: "text-sm text-gray-500 ml-12" }, "Mengganti dana talangan petugas")
                                                        ]) : m("div", { class: "p-4 border-2 border-gray-100 rounded-lg opacity-50 bg-gray-50 cursor-not-allowed" }, [
                                                            m("div", { class: "font-medium text-gray-400" }, "Penggantian"),
                                                            m("div", { class: "text-xs" }, "Tidak tersedia (Saldo Positif)")
                                                        ])
                                                    ]),

                                                    // Input Fields (Amount & Description)
                                                    this.formData.transaction_type && m("div", { class: "space-y-4 animate-fade-in" }, [
                                                        m("div", [
                                                            m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Jumlah"),
                                                            m("input", {
                                                                type: "text",
                                                                class: "w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white",
                                                                placeholder: "Masukkan jumlah...",
                                                                value: this.formData.amountDisplay,
                                                                oninput: e => {
                                                                    const rawValue = e.target.value;
                                                                    const unformatted = this.unformatNumberInput(rawValue);
                                                                    this.formData.amount = parseFloat(unformatted.replace(',', '.')) || '';
                                                                    this.formData.amountDisplay = this.formatNumberInput(unformatted);
                                                                }
                                                            }),
                                                            this.formData.transaction_type === 'reimbursement' &&
                                                            m("p", { class: "text-xs text-gray-500 mt-1" }, `Defisit maksimal: ${this.formatCurrency(Math.abs(balance))}`)
                                                        ]),

                                                        m("div", [
                                                            m("label", { class: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Keterangan"),
                                                            m("textarea", {
                                                                class: "w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white",
                                                                rows: 3,
                                                                placeholder: "Contoh: Uang transport, Dana operasional bulan ini...",
                                                                value: this.formData.description,
                                                                oninput: e => this.formData.description = e.target.value
                                                            })
                                                        ]),

                                                        m("div", { class: "flex justify-end gap-3 pt-4" }, [
                                                            m("button", {
                                                                class: "px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50",
                                                                onclick: () => this.selectCoordinator(null)
                                                            }, "Batal"),
                                                            m("button", {
                                                                class: "px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 shadow-sm disabled:opacity-50",
                                                                onclick: () => this.submitTransaction(),
                                                                disabled: this.submitting
                                                            }, this.submitting ? "Menyimpan..." : "Simpan Transaksi")
                                                        ])
                                                    ])
                                                ])
                                            ])
                                        ])
                                    ]));
                                }

                                return acc;
                            }, [])
                        ])
                    ])
                ])
            ])
        ]);
    }
};

export default CoordinatorFundManagement;